import React, { useEffect, useState } from 'react';
import { forEach, intersectionBy, sortBy } from 'lodash';
import gql from 'graphql-tag';
import { useStoreState } from 'easy-peasy';
import moment from 'moment-timezone';
import uuidv4 from 'uuid/v4';

import client from '../../../utils/apolloClient';
import {
  ErrorMessage,
  Input,
  TagInputs,
  Select,
  MobileInput,
  CheckBox,
} from '../../../components/elements';
import { getUserRoles } from '../../../utils/fetchStatic';
import { ACLSConsumer } from '../../../utils/aclsContext';
import { chunkArray, usePlaceIds } from './helpers';
import AssignPlacesModal from './AssignPlacesModal';

const status = [
  { value: 'ACTIVE', label: 'ACTIVE' },
  { value: 'INACTIVE', label: 'INACTIVE' },
];

const usersQuery = gql`
  query searchUsers($input: SearchInput) {
    search_users(input: $input) {
      user_listing {
        user_id
        display_name
        first_name
        last_name
        full_name
        email
      }
    }
  }
`;

const parseRolesWithLabels = (userCmsRoles, assign_roles) => {
  const rolesWithLabels = [];

  forEach(assign_roles, item => {
    const filteredRoles = userCmsRoles.filter(edge => edge.value === item);
    if (filteredRoles.length !== 0) {
      rolesWithLabels.push({
        value: filteredRoles[0].value,
        label: `${filteredRoles[0].label} (${filteredRoles[0].value})`,
      });
    }
  });

  return sortBy(rolesWithLabels, 'label');
};

const AssignRoles = ({
  assign_roles,
  userCmsRoles,
  role,
  setFieldValue,
  isJustAPartner,
  isOwnAccount,
  errors,
  isAutoCancelKravein,
}) => {
  const roleValues = intersectionBy(
    userCmsRoles,
    role.map(item => ({ value: item })),
    'value',
  );

  const userRoles = parseRolesWithLabels(userCmsRoles, assign_roles);

  return (
    <>
      <TagInputs
        label="Role"
        disabled={(isJustAPartner && isOwnAccount) || isAutoCancelKravein}
        required
        suggestions={userRoles}
        value={roleValues}
        onChange={value => {
          if (value === null) {
            setFieldValue('access', { place_id: [] });
            setFieldValue('places', []);
            setFieldValue('role', []);
          } else {
            const roleInput = value.map(item => item.value);
            setFieldValue('role', roleInput);
          }
        }}
      />
      {errors.role && <ErrorMessage message={errors.role} />}
    </>
  );
};

const filterMap = (map, obj, check) => {
  const temp = new Map();

  if (Array.isArray(check) && check.length === 0) {
    // pass
  } else {
    Array.from(map.keys()).forEach(ele => {
      if (obj[ele]) {
        temp.set(ele, map.get(ele));
      }
    });
  }

  return temp;
};

const truncateString = str =>
  str.length > 30 ? `${str.slice(0, 30)}...` : str;

const PlaceDetails = ({
  values,
  touched,
  errors,
  setFieldValue,
  handleChange,
  user: userValue,
}) => {
  const [userCmsRoles, setUserCMSRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const { user } = useStoreState(state => state.auth);
  const [assignModalIsActive, setAssignModalIsActive] = useState(false);
  const disableStatus = user.user_id === values.user_id;

  const parseData = parseValue => {
    const final = [];
    forEach(parseValue, item => {
      final.push({ value: item.name, label: item.description });
    });
    return final;
  };

  const getUsers = () => {
    const userFilter = userValue.audit.updated_by
      ? [userValue.audit.created_by, userValue.audit.updated_by]
      : [userValue.audit.created_by];
    client.clientPrivate
      .query({
        query: usersQuery,
        variables: {
          input: { filter: { user_filter: { user_id: userFilter } } },
        },
      })
      .then(({ data: { search_users } }) => {
        if (search_users) {
          setUsers(search_users.user_listing);
        }
      });
  };

  useEffect(() => {
    const getData = async () => {
      const roles = await getUserRoles();
      setUserCMSRoles(parseData(roles));
    };
    getData();
    getUsers();
  }, []);

  const role = values.role ? values.role : [];

  const createdBy =
    users.length !== 0 &&
    users.filter(item => item.user_id === userValue.audit.created_by);
  const updatedBy =
    users.length !== 0 &&
    users.filter(item => item.user_id === userValue.audit.updated_by);

  const isJustAPartner = roleArray => {
    const partner = (roleArray ?? []).filter(rl => rl.startsWith('partner'));
    const kravein = (roleArray ?? []).filter(rl => rl.startsWith('kravein'));

    let isPartner = false;

    if (kravein.length !== 0) {
      isPartner = false;
    } else if (partner.length !== 0) {
      isPartner = true;
    }

    return isPartner;
  };

  const isAutoCancelKravein =
    values.email === 'autocancel.kravein@kravein.com.au';

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refetchPlaceData, setRefetchPlaceData] = useState(() => uuidv4());

  const placeIds = React.useMemo(
    () =>
      values?.access?.place_id && Array.isArray(values?.access.place_id)
        ? values?.access.place_id
        : [],
    [values?.access],
  );

  const [placeIdsObjs, setPlaceIdsObjs] = useState([]);

  const placeIdsSelectionStatus = (() => {
    const temp = {};

    placeIdsObjs.forEach(ele => {
      temp[ele.id] = ele.isSelected;
    });

    return temp;
  })();

  useEffect(() => {
    if (placeIds.length === 0) {
      // do nothing
    } else {
      setPlaceIdsObjs(placeIds.map(ele => ({ id: ele, isSelected: true })));
    }
  }, [placeIds]);

  const allIsSelected = new Set(placeIds).has('ALL');

  const paginatedPlaceIds = chunkArray(
    placeIds.filter(ele => ele !== 'ALL'),
    20,
  );

  const [placeDataLoading, placeData] = usePlaceIds({
    placeIds: paginatedPlaceIds,
    refetchPlaceData,
  });

  return (
    <div>
      {assignModalIsActive && (
        <AssignPlacesModal
          isActive={assignModalIsActive}
          onClose={() => setAssignModalIsActive(false)}
          isSubmitting={isSubmitting}
          onSubmit={async formValues => {
            setIsSubmitting(true);

            const { places, allIsSelected: allIsSelectedRef } = formValues;

            let temp = [];

            if (allIsSelectedRef) {
              temp = ['ALL'];
            } else {
              temp = Array.from(places.values()).map(ele => ele.place_id);
            }

            setRefetchPlaceData(uuidv4());
            setFieldValue('access', { place_id: temp });
            setIsSubmitting(false);
            setAssignModalIsActive(false);
          }}
          initialsItems={filterMap(
            placeData,
            placeIdsSelectionStatus,
            placeIds,
          )}
          allIsSelected={allIsSelected}
        />
      )}
      <div className="columns is-multiline">
        {/* <div className="column is-half">
          <CheckBox
            label="Service Account"
            name="is_service_account"
            value={values.is_service_account}
            onChange={value => setFieldValue('is_service_account', value)}
          />
        </div> */}
        {/* <div className="column is-half">&nbsp;</div> */}
        <div className="column is-half">
          <Input label="Login" value={values.email} required disabled />
        </div>
        <div className="column is-half">
          <ACLSConsumer>
            {({ role: ctxRole }) => (
              <Select
                label="Status"
                options={status}
                value={[{ value: values.status, label: values.status }]}
                onChange={({ value }) => setFieldValue('status', value)}
                errors={errors.status}
                touched={touched.status}
                required
                disabled={
                  isJustAPartner(ctxRole) ||
                  isAutoCancelKravein ||
                  disableStatus
                }
              />
            )}
          </ACLSConsumer>
        </div>
        <div className="column is-half">
          <Input
            label="Name"
            value={`${values.first_name} ${values.last_name}`}
            required
            disabled
          />
        </div>
        <div className="column is-half">
          <Input
            label="Display Name"
            required
            value={values.display_name}
            disabled
          />
        </div>
        <div className="column is-half">
          <MobileInput
            label="Contact Phone"
            name="primaryContact"
            id="UserManagement_AddEditUser_PrimaryEmail"
            value={values.primaryContact}
            onChange={value => setFieldValue('primaryContact', value)}
            errors={errors.primaryContact}
            touched={touched.primaryContact}
          />
        </div>
        <div className="column is-half">
          <Input
            label="Contact Email"
            value={values.email}
            name="email"
            onChange={handleChange}
            placeholder="Email"
            disabled
          />
        </div>

        <div className="column is-half">
          <ACLSConsumer>
            {({ assignRoles, role: ctxRole }) => (
              <AssignRoles
                isAutoCancelKravein={isAutoCancelKravein}
                assign_roles={assignRoles}
                isJustAPartner={isJustAPartner(ctxRole)}
                isOwnAccount={user?.email === values.email}
                role={role}
                userCmsRoles={userCmsRoles}
                setFieldValue={setFieldValue}
                errors={errors}
              />
            )}
          </ACLSConsumer>
        </div>
        <div
          style={{
            position: 'relative',
            top: '30px',
          }}
          className="column is-half is-flex is-flex-direction-column"
        >
          <button
            className="button is-primary is-dark is-halfwidth"
            onClick={() => setAssignModalIsActive(true)}
            disabled={placeDataLoading}
          >
            Assign places
          </button>
          <span style={{ color: '#00AEEF', textDecoration: 'underline' }}>
            {(() => {
              let res = '';

              if (placeIds.length !== 0) {
                res = `${placeIds.length} place(s) assigned`;
              }

              if (allIsSelected) {
                res = 'All places are assigned.';
              }

              return res;
            })()}
          </span>
          {!allIsSelected && placeDataLoading && (
            <span style={{ color: '#00AEEF' }}>Loading...</span>
          )}
          {!allIsSelected && !placeDataLoading && (
            <div
              style={{
                maxHeight: '100px',
                overflowY: 'scroll',
              }}
            >
              <div style={{ width: '100%' }}>
                <div>
                  {placeIds.map(ele => (
                    <button
                      className="button"
                      style={{
                        padding: '0.5rem',
                        backgroundColor: '#00b28c',
                        color: 'white',
                      }}
                      onClick={() => {
                        const temp = [...placeIds].filter(ele2 => ele2 !== ele);
                        setPlaceIdsObjs(prev => {
                          const temp2 = [];

                          prev.forEach(ele2 => {
                            if (ele2.id === ele) {
                              temp2.push({ id: ele, isSelected: false });
                            } else {
                              temp2.push(ele2);
                            }
                          });

                          return temp2;
                        });
                        setFieldValue('access', { place_id: temp });
                      }}
                    >
                      {placeData.get(ele) && placeData.get(ele).name ? (
                        <span>{truncateString(placeData.get(ele).name)}</span>
                      ) : (
                        <span>{ele}</span>
                      )}
                      <span className="icon is-small">
                        <i className="fas fa-times" />
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="column is-half">
          <CheckBox
            label="Can Onboard a Place"
            name="onboarding_form_access"
            value={values.onboarding_form_access}
            onChange={value => setFieldValue('onboarding_form_access', value)}
          />
        </div>

        <div className="column is-half">
          <TagInputs label="Companies" />
        </div>
        <div className="column is-half">
          <TagInputs label="Events" />
        </div>

        <div className="column is-half">
          <TagInputs label="Countries" />
        </div>
        <div className="column is-half">
          <TagInputs label="States" />
        </div>
        <div className="column is-half">
          <Input
            label="Created By"
            disabled
            value={
              (createdBy &&
                createdBy.length !== 0 &&
                createdBy[0].display_name) ||
              '---'
            }
          />
        </div>
        <div className="column is-half">
          <Input
            label="Created At"
            disabled
            value={moment(userValue.audit.created_at).format('LLL')}
          />
        </div>
        <div className="column is-half">
          <Input
            label="Updated By"
            disabled
            value={
              (updatedBy &&
                updatedBy.length !== 0 &&
                updatedBy[0].display_name) ||
              '---'
            }
          />
        </div>
        <div className="column is-half">
          <Input
            label="Updated At"
            disabled
            value={
              userValue.audit.updated_at &&
              moment(userValue.audit.updated_at).format('LLL')
            }
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(PlaceDetails);
